import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Entity } from '../supabase/entities';
import * as entitiesService from '../supabase/entities';
import { supabase } from '../supabase/supabase';

// Define cache times - using the same aggressive caching as other queries
const HOUR_IN_MS = 1000 * 60 * 60;
const DAY_IN_MS = HOUR_IN_MS * 24;
const STALE_TIME = DAY_IN_MS * 30; // 30 days stale time
const GC_TIME = DAY_IN_MS * 60; // 60 days garbage collection

// Define query keys for better cache management
export const entityKeys = {
  all: ['entities'] as const,
  lists: () => [...entityKeys.all, 'list'] as const,
  list: (filters: any = {}) => [...entityKeys.lists(), filters] as const,
  byProject: (projectId: string) =>
    [...entityKeys.lists(), { projectId }] as const,
  details: () => [...entityKeys.all, 'detail'] as const,
  detail: (id: number | string) => [...entityKeys.details(), String(id)] as const,
};

/**
 * Hook to fetch all entities for a project
 * @param projectId The project ID to fetch entities for
 * @param options Additional options for the query
 */
export function useProjectEntities(
  projectId: string | undefined,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: entityKeys.byProject(projectId || ''),
    queryFn: async () => {
      if (!projectId) return [];
      return entitiesService.getProjectEntities(projectId);
    },
    enabled: !!projectId && (options.enabled !== false),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Hook to fetch a single entity by ID
 * @param entityId The entity ID to fetch
 * @param options Additional options for the query
 */
export function useEntity(
  entityId: number | string | undefined,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: entityKeys.detail(entityId || ''),
    queryFn: async () => {
      if (!entityId) throw new Error('Entity ID is required');

      try {
        const entity = await entitiesService.getEntityById(entityId);

        // If entity is null (not found), return a default entity object
        if (!entity) {
          console.warn(`Entity with ID ${entityId} not found or access denied, returning default entity`);
          return {
            id: Number(entityId),
            name: 'Unknown Entity',
            description: null,
            status: 'unknown',
            template_id: 0,
            category_id: 0,
            created_at: new Date().toISOString(),
            updated_at: null,
            template_name: 'Unknown',
            template_type: 'Universal',
            category_name: 'Unknown'
          } as Entity;
        }

        return entity;
      } catch (error) {
        console.error('Error in useEntity query:', error);
        throw error;
      }
    },
    enabled: !!entityId && (options.enabled !== false),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Hook to fetch just the name of an entity
 * @param entityId The entity ID to fetch
 * @param options Additional options for the query
 */
export function useEntityName(
  entityId: number | string | undefined,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: entityKeys.detail(entityId || ''),
    queryFn: async () => {
      if (!entityId) throw new Error('Entity ID is required');

      const { data, error } = await supabase
        .from('entities')
        .select('name')
        .eq('id', entityId)
        .single();

      if (error) {
        console.error('Error fetching entity name:', error);

        // Handle specific error cases
        if (error.code === 'PGRST116') {
          console.warn(`Entity with ID ${entityId} not found or access denied`);
          // Return a default object instead of throwing for "no rows returned" error
          return { name: 'Unknown Entity' };
        }

        throw error;
      }
      return data as { name: string };
    },
    enabled: !!entityId && (options.enabled !== false),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Hook to create a new entity
 */
export function useCreateEntity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      data
    }: {
      projectId: string;
      data: {
        name: string;
        description?: string;
        status?: string;
        template_id: number;
        category_id: number;
        field_values?: Record<string, any>;
      };
    }) => {
      return entitiesService.createEntity(projectId, data);
    },
    onSuccess: (_, variables) => {
      // Invalidate the project entities query
      queryClient.invalidateQueries({
        queryKey: entityKeys.byProject(variables.projectId),
      });
    },
  });
}

/**
 * Hook to update an entity
 */
export function useUpdateEntity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      entityId,
      data
    }: {
      entityId: number | string;
      data: Partial<Entity>;
    }) => {
      return entitiesService.updateEntity(entityId, data);
    },
    onSuccess: (_, variables) => {
      // Invalidate the specific entity query
      queryClient.invalidateQueries({
        queryKey: entityKeys.detail(variables.entityId),
      });

      // We don't know the project ID here, so we can't invalidate the project entities query directly
      // Instead, we'll invalidate all entity lists
      queryClient.invalidateQueries({
        queryKey: entityKeys.lists(),
      });
    },
  });
}

/**
 * Hook to delete an entity
 */
export function useDeleteEntity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      entityId,
      projectId
    }: {
      entityId: number | string;
      projectId?: string | number;
    }) => {
      await entitiesService.deleteEntity(entityId, projectId);
      return { entityId, projectId };
    },
    onSuccess: ({ entityId, projectId }) => {
      // Remove the entity from the cache
      queryClient.removeQueries({
        queryKey: entityKeys.detail(entityId),
      });

      // If project ID is provided, invalidate the project entities query
      if (projectId) {
        queryClient.invalidateQueries({
          queryKey: entityKeys.byProject(projectId.toString()),
        });
      } else {
        // Otherwise, invalidate all entity lists
        queryClient.invalidateQueries({
          queryKey: entityKeys.lists(),
        });
      }
    },
  });
}
